import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { lazy, Suspense } from "react";

const SmoothScroll = lazy(() => import("@/components/motion/SmoothScroll").then(m => ({ default: m.SmoothScroll })));
const ScrollProgress = lazy(() => import("@/components/motion/ScrollProgress").then(m => ({ default: m.ScrollProgress })));
const CookieConsent = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Siti Web in 48 Ore | FlashWeb Agency" },
      { name: "description", content: "Creiamo siti web professionali in 48 ore a tariffa fissa. Design moderno, SEO inclusa, hosting configurato e consegna garantita." },
      { name: "author", content: "FlashWeb Agency" },
      { property: "og:title", content: "Siti Web in 48 Ore | FlashWeb Agency" },
      { property: "og:description", content: "Creiamo siti web professionali in 48 ore a tariffa fissa. Design moderno, SEO inclusa, hosting configurato e consegna garantita." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Siti Web in 48 Ore | FlashWeb Agency" },
      { name: "twitter:description", content: "Creiamo siti web professionali in 48 ore a tariffa fissa. Design moderno, SEO inclusa, hosting configurato e consegna garantita." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/CsrkvcfQsFgTQbLNHYwqRzbKA2d2/social-images/social-1781874004744-Social_Image_FlashWeb.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/CsrkvcfQsFgTQbLNHYwqRzbKA2d2/social-images/social-1781874004744-Social_Image_FlashWeb.webp" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "icon", href: "/favicon.ico" },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.flash-web.ch/#organization",
              name: "FlashWeb Agency",
              alternateName: ["FlashWeb", "Flash Web"],
              url: "https://www.flash-web.ch/",
              logo: "https://www.flash-web.ch/favicon.ico",
              description:
                "Agenzia web svizzera: siti web professionali consegnati in 48 ore a tariffa fissa.",
              areaServed: ["CH", "IT"],
              contactPoint: {
                "@type": "ContactPoint",
                contactType: "customer support",
                telephone: "+41-78-236-92-00",
                availableLanguage: ["it", "en", "de", "fr"],
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://www.flash-web.ch/#website",
              name: "FlashWeb Agency",
              url: "https://www.flash-web.ch/",
              publisher: { "@id": "https://www.flash-web.ch/#organization" },
              inLanguage: "it",
            },
            {
              "@type": "ItemList",
              "@id": "https://www.flash-web.ch/#sitenav",
              name: "Navigazione principale FlashWeb Agency",
              itemListElement: [
                { "@type": "SiteNavigationElement", position: 1, name: "Servizi", url: "https://www.flash-web.ch/servizi" },
                { "@type": "SiteNavigationElement", position: 2, name: "Prezzi", url: "https://www.flash-web.ch/prezzi" },
                { "@type": "SiteNavigationElement", position: 3, name: "Come funziona", url: "https://www.flash-web.ch/come-funziona" },
                { "@type": "SiteNavigationElement", position: 4, name: "Portfolio", url: "https://www.flash-web.ch/portfolio" },
                { "@type": "SiteNavigationElement", position: 5, name: "Recensioni", url: "https://www.flash-web.ch/recensioni" },
                { "@type": "SiteNavigationElement", position: 6, name: "Chi siamo", url: "https://www.flash-web.ch/chi-siamo" },
                { "@type": "SiteNavigationElement", position: 7, name: "FAQ", url: "https://www.flash-web.ch/faq" },
                { "@type": "SiteNavigationElement", position: 8, name: "Blog", url: "https://www.flash-web.ch/blog" },
                { "@type": "SiteNavigationElement", position: 9, name: "Trasparenza", url: "https://www.flash-web.ch/trasparenza" },
                { "@type": "SiteNavigationElement", position: 10, name: "Contatti", url: "https://www.flash-web.ch/contatti" },
              ],
            },
          ],
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="it">
      <head>
        {/*
          Google Ads / gtag.js — implementazione conforme alle best practice (caricamento differito).
          NOTA: Lighthouse può segnalare "Usa API obsolete" (Attribution Reporting deprecation)
          per https://www.googletagmanager.com/gtag/js. L'avviso proviene dallo script esterno di
          Google e NON è risolvibile nel codice proprietario: il fix dipende da Google stessa.
          Non rimuovere: tracciamento conversioni Google Ads attivo (AW-18254074710).
        */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;
              gtag('js', new Date());
              gtag('config', 'AW-18254074710');
              (function(){
                var loaded = false;
                function loadGtm(){
                  if (loaded) return; loaded = true;
                  var s = document.createElement('script');
                  s.async = true;
                  s.src = 'https://www.googletagmanager.com/gtag/js?id=AW-18254074710';
                  document.head.appendChild(s);
                  ['pointerdown','keydown','touchstart','scroll'].forEach(function(e){
                    window.removeEventListener(e, loadGtm, { passive: true });
                  });
                }
                ['pointerdown','keydown','touchstart','scroll'].forEach(function(e){
                  window.addEventListener(e, loadGtm, { passive: true, once: true });
                });
                setTimeout(loadGtm, 3500);
              })();
            `,
          }}
        />


        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={null}>
        <SmoothScroll />
        <ScrollProgress />
        <CookieConsent />
      </Suspense>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>

  );
}
